/**
 * The "PA UX" utilities live within the `porfolio-ui` repository. And are primarily
 * meant for use within the new UX. There are a handful which will be useful within
 * the context of "PA Core" and are added onto `window.paux`
 */
namespace Solovis.UX.Utils {
  /* init, loading, done, error */
  let loadingState = 0;

  /**
   * Internal method for checking to see if necessary resources for the
   * new experience have been loaded
   *
   * @returns whether the new dependencies are loaded
   */
  async function areNewUxDependenciesLoaded(): Promise<boolean> {
    if (loadingState === 0) {
      loadNewExperienceDependencies();
    }

    await until(() => loadingState >= 2, 100)

    if (loadingState === 3) {
      throw new Error('Error occurred loading new experience dependencies');
    }

    return true;
  }

  /**
   * Dependency loader for the new experience dependencies.
   *
   * @returns true once loaded
   */
  export async function loadNewExperienceDependencies() {
    if (!isNewUxEnabled()) {
      return false;
    }

    if (loadingState > 0) {
      return areNewUxDependenciesLoaded();
    }

    loadingState = 1;

    try {
      const reactPromise = importShim('react');
      const reactDOMPromise = importShim('react-dom')
      const paUIComponentsPromise = importShim("@solovis/pa-ui-components")
      const paUtilsPromise = importShim('@solovis/pa-utils');

      await reactPromise;
      await reactDOMPromise;
      await paUIComponentsPromise;
      const utils = await paUtilsPromise;

      (window as any).paux = (utils as any).paux;
      loadingState = 2;
    } catch (error) {
      loadingState = 3;
      throw new Error(error)
    }

    return true;
  }

  /**
   * Handles initializing the new experience.
   *
   * @returns a promise
   */
  export function initialize() {
    const beforeUnloadListener = () => {
      const { document } = window;
      if (window.onbeforeunload && window.onbeforeunload(event)) {
        return;
      }
      (document.querySelector('body') as HTMLBodyElement).classList.add('out')
      window.removeEventListener('beforeunload', unloadListener)
    }
    window.addEventListener('beforeunload', beforeUnloadListener)

    const unloadListener = () => {
      const { document } = window;
      (document.querySelector('body') as HTMLBodyElement).classList.remove('out')
      window.removeEventListener('unload', unloadListener)
    }
    window.addEventListener('unload', unloadListener)

    return importShim<{}, any>("@solovis/pa-utils").then(
      ({ paux }) => {
        (window as any).paux = paux;
        if (Solovis.UX.Utils.isNewUxEnabled()) {
          importShim<{}, any>("@solovis/root-config");
          // TODO: look at calling loadNewExperienceDependencies here         

          const listener = () => {
            const { document } = window;
            (document.querySelector('body') as HTMLBodyElement).classList.add('in');
            window.removeEventListener("@solovis/app/loaded", listener);
          }
          window.addEventListener("@solovis/app/loaded", listener)

          // NOTE: Loader could be created here if desired v. markup

          postInit();
        }
        placeToastComponent();
      },
      (error) => {
        console.error("New experience not loaded.");
        (window as any).paux = null;
      }
    );
  }

  /**
   * Directly trying to reference `paux` will throw an error
   * so by wrapping it here we can do a quick check if things
   * are loaded/available so we can fail fast
   *
   * @returns whether or not the paux utils are available
   */
  function isLoaded(): boolean {
    try {
      if (paux || paux === null) {
        return true;
      }
    } catch (e) {
      return false;
    }
  }

  function placeToastComponent() {
    const toastProviderContainer = document.createElement("div");
    toastProviderContainer.classList.add("paux");
    document.body.appendChild(toastProviderContainer);

    Solovis.UX.Utils.placeComponent("ToastProvider", toastProviderContainer);
  }

  //tossed in for now to allow waiting for loading rather than returning false in isNewUxEnabled
  export function until(conditionFunction: () => boolean, maxRetries = 10) {
    let retries = 0;
    const poll = (resolve, reject) => {
      if (maxRetries === retries) {
        reject('Max retries reached');
      }

      if (conditionFunction()) {
        resolve();
      } else {
        setTimeout((_) => {
          retries++;
          poll(resolve, reject);
        }, 400);
      }
    };

    return new Promise(poll);
  }

  export function isExcelPlugin() {
    // TODO: when we make the new experience primary we'll need to know if the plugin is running!
    // NOTE: _Layout.cshtml could set this value while we come up with a more holistic approach
    const isPlugin = Boolean((window as any).Solovis_IsExcelPlugin);
    return isPlugin;
  }

  /**
   * Utility function for checking if the user has the new experience enabled.
   *
   * @returns whether the user should see the new experience
   */
  export function isNewUxEnabled(): boolean {
    // NOTE: due to possible race conditions paux could be unavailable when this is called.
    // TODO: revist/recheck this, perhaps now no longer the case
    const storedValue = window.localStorage.getItem('pa//ux');
    const value = storedValue ? storedValue === 'true' :
      Boolean((Solovis as any).Info.userEnabledForNewExperienceByDefault);
    return value;
  }

  /**
   * Utility function for checking if the user is eligible for the
   * new report header.
   *
   * @returns whether is this user eligible for the new report header
   */
  export function isUserEligibleForNewReportHeader(): boolean {
    const enabled = isNewUxEnabled();
    const isSlim = (Solovis as any).Info.layoutStyle == "Slim";
    return enabled && !isSlim && !isKeyReport();
  }

  /**
   * Utility function for toggling the state of the new experience.
   *
   * @param state whether to enable/disable the new experience
   * @returns whether the update was successful.
   */
  export function setUxState(state: boolean): Promise<boolean> {
    if (!isLoaded() || paux === null) {
      return Promise.reject(false);
    }

    return paux.setNewUxEnabled(state);
  }

  export async function getComponent(componentName: string, params?: any) {
    //import required items
    return await Promise.all([
      // NOTE: due to missing types (es6/dom related) we pulled the React types
      // ideally this could be importShim<{}, typeof React>("react")
      importShim<{}, any>("react"),
      importShim("@solovis/pa-ui-components"),
    ]).then(([react, components]) => {
      const component = react.createElement(components[componentName], {
        ...params,
      });
      return component;
    });
  }

  export async function placeComponent(
    componentName: string,
    target: HTMLElement,
    params: any = {}
  ): Promise<string> {
    //allow an id to be passed in rather than generated
    const { id = this.generateId(), className = '' } = params;

    const defaultStyles = {
      'ConfigureControl': 'tw-w-full',
      'Input': 'tw-w-full',
    }

    // NOTE: due to missing types (es6/dom related) we pulled the React types
    // ideally this would be importShim<{}, typeof ReactDOM>("react-dom")
    const reactDOM = await importShim<{}, any>("react-dom");

    const component = await this.getComponent(componentName, {
      ...params,
      id,
      className: `${defaultStyles[componentName] || ''} ${className}`,
    });

    reactDOM.render(component, target);
    return id;
  }

  export async function renderModal(modalName: string, props: Record<string, any> = {}, target = document.body) {
    const modalContainer = document.createElement("div");
    modalContainer.classList.add("paux");
    target.appendChild(modalContainer);

    let modalRef;
    const { ref = (ref) => modalRef = ref, onClose } = props;

    await Solovis.UX.Utils.placeComponent(modalName, modalContainer, {
      ...props, ref, onClose: () => {
        onClose?.();
        modalContainer.remove();
      }
    });

    return {
      open: async () => {
        modalRef.open();
      },
      close: async () => {
        modalRef.close();
      }
    }
  }

  export async function renderConfirmationModal(props: Record<string, any>, target?: HTMLElement) {
    return await Solovis.UX.Utils.renderModal("ConfirmationModal", props, target);
  }

  export async function renderWarningModal(props: Record<string, any>, target?: HTMLElement) {
    return await Solovis.UX.Utils.renderModal("WarningModal", props, target);
  }

  export async function renderErrorModal(props: Record<string, any>, target?: HTMLElement) {
    return await Solovis.UX.Utils.renderModal("ErrorModal", props, target);
  }

  /**
   * Utility function for generating ids to interact with react from jquery
   *
   * @param prefix if you wish to prefix the generated id with something readable
   * @returns generated id
   */
  export function generateId(prefix?: string): string {
    return paux.generateId(prefix);
  }

  /**
   * Utility function for generating the string for a tooltip from the paramInfo 
   *
   * @param paramInfo for the label that you want to create a tooltip string for
   * @returns generated string for displaying in the tooltip
   */
  export function getTooltipFromParamInfo(paramInfo: {
    label: string;
    name: string;
    parameterType?: string;
    list?: boolean;
  }): string {
    return paux.getTooltipFromParamInfo(paramInfo);
  }


  function postInit() {
    const enabled = isNewUxEnabled();

    const sideBarWidth = "64px";
    if (enabled) {
      const { document } = window;

      // _TitleBar.cshtml
      // NOTE: should be able to use HTMLElement... TS version
      const titleBar = document.querySelector<any>(".header-title-bar-container");
      if (titleBar) {
        titleBar.style.paddingLeft = sideBarWidth;
      }

      // _Header.cshtml
      // NOTE: should be able to use HTMLElement... TS version
      const secondaryNav = document.querySelector<any>(".header-lower-container");
      if (secondaryNav) {
        secondaryNav.style.display = "none";
      }

      // NOTE: should be able to use HTMLElement... TS version
      const body = document.querySelector<any>("#body")
      if (body) {
        body.style.marginLeft = sideBarWidth;
      }
    }
  }

  export function areNewControlsSupported() {
    if (isExcelPlugin()) {
      return false;
    }
    // NOTE: ReportConfig + ReportConfigControl can access - "renderWithNewControls" on the REPORT OPTIONS obj
    // TODO: how can we leverage the report option above instead of needing a check everywhere
    const enabled = isNewUxEnabled();
    return enabled && (isKeyReport() || isDashboard());
  }
  export function isKeyReport(): boolean {
    try {
      const pageViewModel = (window as any).PageViewModel;
      return pageViewModel.reportTypeId === 6;
    } catch (e) {
      return false;
    }
  }

  export function isDashboard(): boolean {
    try {
      const pageViewModel = (window as any).PageViewModel;
      return pageViewModel.reportTypeId === 4;
    } catch (e) {
      return false;
    }
  }

  let activelyLoadingCount = 0;

  export function showLoader() {
    // "showLoader" can be called multiple times from multiple places
    // to facilitate using a single loader for multiple active tasks
    activelyLoadingCount++;
    if (activelyLoadingCount > 1) {
      return;
    }

    // NOTE: here you would show the loader (also advisable to disable scrolling)
    // $("body").css("overflow", "hidden");
  }

  export function hideLoader() {
    // delay .125s to allow for instances where another call might start shortly after completion
    // of the "last load". This way the next call/showLoader would occur prior to its removal
    const timerId = setTimeout(() => {

      activelyLoadingCount--;
      if (activelyLoadingCount < 1) {
        activelyLoadingCount = 0;

        // NOTE: here you would hide the loader and perhaps re-enable scrolling too
        // if using a timed transition should use the same delay or something close like below
        // setTimeout(() => $("body").css("overflow", ""), 150);
      }
    }, 125);
  }

  export function emit(eventName: string) {
    if (!Solovis.UX.Utils.isNewUxEnabled()) return;
    window.dispatchEvent(new Event(eventName));
  }
  export function subscribe(eventName: string, listener: () => void) {
    if (!Solovis.UX.Utils.isNewUxEnabled()) return;
    window.addEventListener(eventName, listener);
  }
  export function unsubscribe(eventName: string, listener: () => void) {
    if (!Solovis.UX.Utils.isNewUxEnabled()) return;
    window.removeEventListener(eventName, listener);
  }
}